const EASTERN = 'America/New_York';

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

interface EasternParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function easternParts(date: Date): EasternParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((part) => part.type === type)?.value ?? '0';
    return Number.parseInt(value, 10);
  };
  let hour = read('hour');
  if (hour === 24) hour = 0;
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour,
    minute: read('minute'),
  };
}

export function easternDateKey(date: Date): string {
  const parts = easternParts(date);
  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  return `${parts.year}-${month}-${day}`;
}

/** Interpret a YYYY-MM-DD hour:minute as Eastern wall time and return the UTC instant. */
export function easternWallClockToUtc(dateKey: string, hour: number, minute: number): Date {
  const [year, month, day] = dateKey.split('-').map((part) => Number.parseInt(part, 10));
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);
  const shown = easternParts(new Date(utcGuess));
  const shownAsUtc = Date.UTC(shown.year, shown.month - 1, shown.day, shown.hour, shown.minute);
  return new Date(utcGuess - (shownAsUtc - utcGuess));
}

export function addEasternDays(dateKey: string, days: number): string {
  const noon = easternWallClockToUtc(dateKey, 12, 0).getTime() + days * 24 * 60 * 60 * 1000;
  return easternDateKey(new Date(noon));
}

function weekdayIndex(dateKey: string): number {
  const label = easternWallClockToUtc(dateKey, 12, 0).toLocaleDateString('en-US', {
    timeZone: EASTERN,
    weekday: 'short',
  });
  return WEEKDAY_INDEX[label] ?? 1;
}

/** Matches shared leasing-booking weekend discovery-call lead time. */
export const DISCOVERY_CALL_WEEKEND_MIN_LEAD_MS = 2 * 60 * 60 * 1000;

export function discoveryCallSlotIsBookable(startAtIso: string, dateKey: string, nowMs: number): boolean {
  const startMs = new Date(startAtIso).getTime();
  if (startMs <= nowMs) return false;
  const weekday = weekdayIndex(dateKey);
  if (weekday === 0 || weekday === 6) {
    return startMs >= nowMs + DISCOVERY_CALL_WEEKEND_MIN_LEAD_MS;
  }
  return true;
}

export function easternMondayKey(anchor: Date = new Date()): string {
  const dateKey = easternDateKey(anchor);
  const weekday = weekdayIndex(dateKey);
  const daysToMonday = weekday === 0 ? 6 : weekday - 1;
  return addEasternDays(dateKey, -daysToMonday);
}

export function easternWeekDateKeys(mondayKey: string): string[] {
  return Array.from({ length: 7 }, (_, index) => addEasternDays(mondayKey, index));
}

export function easternMinutesFromMidnight(date: Date): number {
  const parts = easternParts(date);
  return parts.hour * 60 + parts.minute;
}

export function formatEasternHourLabel(hour: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12} ${period}`;
}

export function formatEasternClock(iso: string, withZone = false): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    hour: 'numeric',
    minute: '2-digit',
    ...(withZone ? { timeZoneName: 'short' as const } : {}),
  }).format(new Date(iso));
}

export function formatEasternWeekRange(mondayKey: string): string {
  const sundayKey = addEasternDays(mondayKey, 6);
  const start = easternWallClockToUtc(mondayKey, 12, 0);
  const end = easternWallClockToUtc(sundayKey, 12, 0);
  const startLabel = start.toLocaleDateString('en-US', {
    timeZone: EASTERN,
    month: 'short',
    day: 'numeric',
  });
  const endLabel = end.toLocaleDateString('en-US', {
    timeZone: EASTERN,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startLabel} – ${endLabel}`;
}

export function formatEasternDayHeader(dateKey: string): { weekday: string; date: string } {
  const utc = easternWallClockToUtc(dateKey, 12, 0);
  return {
    weekday: utc.toLocaleDateString('en-US', { timeZone: EASTERN, weekday: 'short' }),
    date: utc.toLocaleDateString('en-US', { timeZone: EASTERN, month: 'short', day: 'numeric' }),
  };
}

export function easternZoneAbbrev(date: Date = new Date()): string {
  const part = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN,
    timeZoneName: 'short',
  })
    .formatToParts(date)
    .find((item) => item.type === 'timeZoneName');
  return part?.value ?? 'ET';
}

export function formatEasternLongDate(dateKey: string): string {
  return easternWallClockToUtc(dateKey, 12, 0).toLocaleDateString('en-US', {
    timeZone: EASTERN,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function formatEasternDateLine(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: EASTERN,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function formatEasternMonthYear(year: number, monthIndex: number): string {
  const month = String(monthIndex + 1).padStart(2, '0');
  return easternWallClockToUtc(`${year}-${month}-01`, 12, 0).toLocaleDateString('en-US', {
    timeZone: EASTERN,
    month: 'long',
    year: 'numeric',
  });
}
